const SELECTOR = '[data-wbs-custom-variant-carousel]';

/**
 * @param {Element} customVariantCarousel
 */
function initWbsCustomVariantCarousel(customVariantCarousel) {
  if (
    !(customVariantCarousel instanceof HTMLElement) ||
    customVariantCarousel.dataset.wbsCustomVariantCarouselReady === 'true'
  ) {
    return;
  }

  const track = customVariantCarousel.querySelector('[data-wbs-custom-variant-carousel-track]');
  const prevButton = customVariantCarousel.querySelector('[data-wbs-custom-variant-carousel-prev]');
  const nextButton = customVariantCarousel.querySelector('[data-wbs-custom-variant-carousel-next]');

  if (!(track instanceof HTMLElement)) return;

  customVariantCarousel.dataset.wbsCustomVariantCarouselReady = 'true';

  const updateCardSizing = () => {
    const visibleItemsRaw = window
      .getComputedStyle(customVariantCarousel)
      .getPropertyValue('--wbs-custom-variant-visible-items');
    const visibleItems = Number.parseFloat(visibleItemsRaw) || 4.5;

    const styles = window.getComputedStyle(track);
    const gapPx = Number.parseFloat(styles.gap || styles.columnGap || '0') || 0;

    const trackWidth = track.getBoundingClientRect().width;
    if (!trackWidth || visibleItems <= 0) return;

    // Use floor(visibleItems) full cards; e.g. 4.5 => 4 full cards => 3 visible gaps.
    const fullCards = Math.max(Math.floor(visibleItems), 1);
    const gapCount = Math.max(fullCards - 1, 0);

    const cardWidth = (trackWidth - gapCount * gapPx) / visibleItems;
    if (!Number.isFinite(cardWidth) || cardWidth <= 0) return;

    customVariantCarousel.style.setProperty('--wbs-custom-variant-card-width', `${cardWidth}px`);
  };

  const getCardStep = () => {
    const card = track.querySelector('.variant-option__button-label--wbs-custom-variant-card');
    if (!(card instanceof HTMLElement)) return track.clientWidth * 0.8;

    const styles = window.getComputedStyle(track);
    const gapValue = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gapValue;
  };

  const updateControls = () => {
    const maxScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    const canScroll = maxScroll > 2;

    customVariantCarousel.setAttribute('data-wbs-custom-variant-carousel-scrollable', canScroll ? 'true' : 'false');

    if (!canScroll) {
      if (prevButton instanceof HTMLButtonElement) prevButton.disabled = true;
      if (nextButton instanceof HTMLButtonElement) nextButton.disabled = true;
      return;
    }

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;

    if (prevButton instanceof HTMLButtonElement) prevButton.disabled = atStart;
    if (nextButton instanceof HTMLButtonElement) nextButton.disabled = atEnd;
  };

  const scheduleLayoutAndControls = () => {
    updateCardSizing();
    requestAnimationFrame(() => {
      updateControls();
    });
  };

  prevButton?.addEventListener('click', () => {
    track.scrollBy({ left: -getCardStep(), behavior: 'smooth' });
  });

  nextButton?.addEventListener('click', () => {
    track.scrollBy({ left: getCardStep(), behavior: 'smooth' });
  });

  track.addEventListener('scroll', updateControls, { passive: true });
  window.addEventListener('resize', scheduleLayoutAndControls);

  if (typeof ResizeObserver !== 'undefined') {
    const resizeObserver = new ResizeObserver(() => scheduleLayoutAndControls());
    resizeObserver.observe(track);
  }

  track.querySelectorAll('img').forEach((img) => {
    if (!(img instanceof HTMLImageElement) || img.complete) return;
    img.addEventListener(
      'load',
      () => {
        scheduleLayoutAndControls();
      },
      { once: true },
    );
  });

  const selectedOption = track.querySelector('input:checked');
  if (selectedOption instanceof HTMLElement) {
    selectedOption.closest('.variant-option__button-label--wbs-custom-variant-card')?.scrollIntoView({
      inline: 'center',
      block: 'nearest',
      behavior: 'auto',
    });
  }

  scheduleLayoutAndControls();
}

function initAllWbsCustomVariantCarousels() {
  document.querySelectorAll(SELECTOR).forEach((customVariantCarousel) => initWbsCustomVariantCarousel(customVariantCarousel));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllWbsCustomVariantCarousels);
} else {
  initAllWbsCustomVariantCarousels();
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (!(mutation.target instanceof HTMLElement)) continue;
    if (mutation.addedNodes.length > 0) {
      initAllWbsCustomVariantCarousels();
      break;
    }
  }
});

observer.observe(document.documentElement, { childList: true, subtree: true });
